import {
  Box,
  Button,
  Stack,
  Typography,
  IconButton,
  CircularProgress,
  Alert,
  Tooltip,
} from '@mui/material';
import {
  ChevronRight,
  Close,
  NotificationAdd,
  NotificationsOff,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { useEffect, useState, useCallback, useRef } from 'react';

import useChatClient from 'src/hooks/useChatClient';
import useAuth from 'src/hooks/useAuth';
import { PATH_DASHBOARD } from 'src/routes/paths';
import useResponsive from 'src/hooks/useResponsive';
import { useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/react';
import { useSnackbar } from 'notistack';
import MarketplaceChatListingHeader from 'src/components/marketplace/MarketplaceChatListingHeader';

// ----------------------------------------------------------------------

export default function ChatHeader() {
  const { t } = useTranslation('translation');
  const isDesktop = useResponsive('up', 'md');
  const [isMuteLoading, setMuteLoading] = useState(false);
  const isMountedRef = useRef(true);
  const { user } = useAuth();
  const {
    activeConversation: conversation,
    pendingChat,
    setChatOpen,
    setDrawerOpen,
    setPendingChat,
    toggleMute,
  } = useChatClient();
  const { enqueueSnackbar } = useSnackbar();

  // Set up cleanup on component unmount only
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // The mute flag lives on the user's participant doc and is mirrored onto
  // the normalized conversation by the chat client, so the toggle always
  // reflects the persisted state (including on load).
  const isMuted = !!conversation?.muted;

  const handleToggleMuteConversationNotification = useCallback(
    async (conversation) => {
      if (isMountedRef.current) {
        setMuteLoading(true);
      }

      try {
        await toggleMute(conversation.id);
      } catch (err) {
        enqueueSnackbar(t('chat.mute-toggle-error'), { variant: 'error' });
        Sentry.captureException(err);
      } finally {
        if (isMountedRef.current) {
          setMuteLoading(false);
        }
      }
    },
    [toggleMute, enqueueSnackbar, t]
  );

  if (!conversation && !pendingChat) {
    return null;
  }

  const isArchived = !!conversation?.archived;

  const isMarketplace =
    (conversation?.type || '').toUpperCase() === 'MARKETPLACE_LISTING';
  // The viewer is the seller iff their team owns the listing. Legacy docs
  // without sellerTeamId can't be told apart, so they keep the plain header.
  const isMarketplaceBuyer =
    isMarketplace &&
    !!conversation?.sellerTeamId &&
    String(conversation.sellerTeamId) !== String(user?.team_id);

  const pendingTitle =
    pendingChat?.type === 'team'
      ? `${t('chat.team-chat')}`
      : pendingChat?.type === 'admin'
        ? `${t('chat.admin-chat')}`
        : pendingChat?.title || '';

  const muteToggleLabel = isMuted
    ? t('chat.unmute-notification')
    : t('chat.mute-notification');

  const handleClose = () => {
    if (isDesktop) {
      setChatOpen(false);
      setDrawerOpen(false);
    } else {
      setChatOpen(false);
    }
    setPendingChat(null);
  };

  return (
    <Stack
      direction="row"
      alignItems="center"
      sx={{
        p: (theme) => theme.spacing(2, 1, 2, 2),
      }}
    >
      <Stack direction="column" spacing={0.5} sx={{ minWidth: 0, flexGrow: 1 }}>
        {isMarketplace && conversation?.listingId ? (
          // Rich marketplace header: listing thumbnail, price, status and a
          // link to the listing. The conversation friendlyName is the listing
          // title, so this replaces (and enriches) the plain title above.
          <MarketplaceChatListingHeader
            listingId={conversation.listingId}
            fallbackTitle={conversation.friendlyName}
            showSellerName={isMarketplaceBuyer}
          />
        ) : (
          <Typography>
            {(conversation?.friendlyName || pendingTitle)
              .replace('Admin Chat:', `${t('chat.admin-chat')}:`)
              .replace('Team Chat:', `${t('chat.team-chat')}:`)}
          </Typography>
        )}

        {isArchived && (
          <Box sx={{ maxWidth: 300 }}>
            <Alert
              severity="info"
              variant="outlined"
              sx={{ py: 0.25, px: 1, fontSize: 12, width: '100%' }}
            >
              {t('chat.archived-conversation-banner')}
            </Alert>
          </Box>
        )}
      </Stack>

      <Box flex="1" />

      {conversation?.rescueId ||
      (pendingChat?.type === 'donation' && pendingChat?.id) ? (
        <Button
          endIcon={<ChevronRight />}
          LinkComponent={Link}
          to={PATH_DASHBOARD.viewPartialDonation(
            conversation?.donationId,
            conversation?.rescueId
          )}
        >
          {t('chat.view-partial-donation')}
        </Button>
      ) : null}

      {conversation && (
        <Tooltip title={muteToggleLabel}>
          <IconButton
            aria-label={muteToggleLabel}
            onClick={() =>
              handleToggleMuteConversationNotification(conversation)
            }
          >
            {isMuteLoading ? (
              <CircularProgress size={20} />
            ) : isMuted ? (
              <NotificationAdd />
            ) : (
              <NotificationsOff />
            )}
          </IconButton>
        </Tooltip>
      )}
      <IconButton onClick={handleClose}>
        <Close />
      </IconButton>
    </Stack>
  );
}
